/**
 * The wiring behind „Kto ma którą": three reads, one write per cell, and which column is described.
 *
 * Behaviour in `model/`, wiring here, rendering in the components — so nothing below decides anything
 * a test would want to reach. What lives here is WHEN things are read and what happens when a cell
 * is pressed.
 *
 * ── The grid waits for ALL THREE of its reads ────────────────────────────────────────────────
 * Templates, intake and roster. A grid drawn from the templates alone would have columns and no
 * rows; drawn from the roster alone, rows and no columns. Either is a partial answer wearing the
 * shape of a complete one, and both would reflow when the third landed. One gate over the union.
 *
 * ── The EXPOSED state is the wait branch, tail and all ───────────────────────────────────────
 * `state` and `cardState` say „pending" for as long as the gate says the wait belongs on screen,
 * which outlasts the reads by the skeleton's 300ms minimum life. The tab forks on them and so forks
 * on the gates, and the grid that replaces the bars carries the reveal in its first render.
 *
 * The verdicts the READS give are kept below as `gridAnswer` and `cardAnswer` and are not exposed.
 * The card inherits the grid's RAW verdict, never the gated one: feeding a gated state into the
 * card's own gate would stack a second 300ms tail on top of the grid's. Inheriting the raw one keeps
 * the two gates in lockstep instead, because they open and close on the same render.
 *
 * ── The write is NOT optimistic, and the pressed cell says so ────────────────────────────────
 * `features/session-comment`'s rule, and `useMarkSurveyReviewed`'s: a rollback path is a thing that
 * can be wrong. So the checkbox does NOT flip on press. It goes busy — `aria-busy`, a pending mark —
 * and flips when the refetched list says it flipped. A failed write therefore leaves the checkbox
 * where it was, which is the truth, and the banner says what happened.
 *
 * ── Which cell is busy is tracked by cell, not by mutation ───────────────────────────────────
 * Two mutations (a POST and a DELETE) serve one control, and `isPending` on either says only that
 * SOMETHING is in flight. The trainer can press a second cell while the first is landing, so what
 * the grid needs is which CELLS are open — a set, keyed by template and customer.
 *
 * ── The described column is state, not an address ────────────────────────────────────────────
 * `?tab=` says which of the three tabs is open and nothing else; which column is being described is
 * where the pointer is. The Ankiety tab stands on the same rule and its header carries the argument.
 * It falls back rather than being corrected: a column that leaves the list takes the first of what
 * is left.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import {
  deleteSurveyTemplateIdAssignmentsCustomerId,
  postSurveyTemplateIdAssignments,
} from '~/shared/api/generated/endpoints/survey/survey';

import {
  clientFacesQueryOptions,
  intakeTemplateQueryOptions,
  surveyTemplateQueryOptions,
  surveyTemplatesQueryKey,
  surveyTemplatesQueryOptions,
} from '../api/reportsQueries';
import {
  buildMatrix,
  cellRefusal,
  holdsSurvey,
  type MatrixColumn,
  type MatrixView,
} from '../model/matrix';
import { surveyGroups, type SurveyRow } from '../model/surveyList';
import { surveySections, type DetailSection } from '~/features/survey-question-editor';

/** What the grid is showing. `rows` is reached from exactly one of them. */
export type MatrixState = 'pending' | 'error' | 'noClients' | 'noSurveys' | 'rows';

/** What the card beside it is showing. */
export type MatrixCardState = 'pending' | 'error' | 'ready' | 'none';

/** A cell's identity — the pair a write is addressed by and the pair a pending mark is keyed on. */
function cellKey(templateId: number, customerId: number): string {
  return `${String(templateId)}:${String(customerId)}`;
}

export interface MatrixTabView {
  /** What the grid draws. „pending" covers the whole gated wait, not only the open reads. */
  readonly state: MatrixState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly view: MatrixView;
  /** The check-in the card describes, and the column that is tinted for it. */
  readonly selected: SurveyRow | null;
  readonly onSelectColumn: (id: number) => void;
  readonly cardState: MatrixCardState;
  readonly cardSkeleton: boolean;
  readonly cardReveal: string;
  readonly sections: readonly DetailSection[];
  /** `true` while this cell's own write is in flight. */
  readonly isBusy: (templateId: number, customerId: number) => boolean;
  readonly onToggle: (column: MatrixColumn, customerId: number) => void;
  /** The last write failed. Cleared by the next press. */
  readonly writeFailed: boolean;
  readonly onRetry: () => void;
}

export function useMatrixTab(announce: (message: string) => void): MatrixTabView {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const templates = useQuery(surveyTemplatesQueryOptions());
  const intake = useQuery(intakeTemplateQueryOptions());
  const roster = useQuery(clientFacesQueryOptions());

  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [writeFailed, setWriteFailed] = useState(false);

  const groups = surveyGroups(templates.data, locale);
  const view = buildMatrix(groups, roster.data ?? new Map(), { t, locale });

  // The fallback, not a correction: the described column may have been deleted or paused out from
  // under the trainer, and the first of what is left is what the card then describes.
  const selected =
    groups.checkIns.find((row) => row.id === picked) ?? groups.checkIns[0] ?? null;

  const pending = templates.isPending || intake.isPending || roster.isPending;
  const failed = templates.isError || intake.isError || roster.isError;
  const gridAnswer: MatrixState = pending
    ? 'pending'
    : failed
      ? 'error'
      : view.rows.length === 0
        ? 'noClients'
        : view.columns.length === 0
          ? 'noSurveys'
          : 'rows';
  const gate = useSkeletonGate(gridAnswer === 'pending');
  const state: MatrixState = gate.waiting ? 'pending' : gridAnswer;

  const detail = useQuery(surveyTemplateQueryOptions(selected?.id ?? null));
  // A card with nothing in it because the GRID has not answered is not the same fact as a card with
  // nothing in it because the trainer has no check-ins. The card follows the grid through the wait
  // and only says „none" once the grid has answered and there is still nothing to describe.
  const cardAnswer: MatrixCardState =
    selected === null
      ? gridAnswer === 'pending'
        ? 'pending'
        : gridAnswer === 'error'
          ? 'error'
          : 'none'
      : detail.isPending
        ? 'pending'
        : detail.isError
          ? 'error'
          : 'ready';
  const cardGate = useSkeletonGate(cardAnswer === 'pending');
  const cardState: MatrixCardState = cardGate.waiting ? 'pending' : cardAnswer;

  const release = (key: string) => {
    setBusy((open) => {
      const next = new Set(open);
      next.delete(key);
      return next;
    });
  };

  const write = useMutation({
    mutationFn: ({
      templateId,
      customerId,
      held,
    }: {
      templateId: number;
      customerId: number;
      held: boolean;
    }) =>
      held
        ? deleteSurveyTemplateIdAssignmentsCustomerId(templateId, customerId)
        : postSurveyTemplateIdAssignments(templateId, { customerIds: [customerId] }),
  });

  return {
    state,
    skeleton: gate.skeleton,
    reveal: gate.reveal,
    view,
    selected,
    onSelectColumn: setPicked,
    cardState,
    cardSkeleton: cardGate.skeleton,
    cardReveal: cardGate.reveal,
    // Off the RAW verdict: the card reads these only once it is out of its wait, so computing them
    // a render early costs nothing while keeping this free of the tail.
    sections: cardAnswer === 'ready' ? surveySections(detail.data, t) : [],
    isBusy: (templateId, customerId) => busy.has(cellKey(templateId, customerId)),
    onToggle: (column, customerId) => {
      const key = cellKey(column.id, customerId);
      // A second press while the first is landing would be a second request for the same cell whose
      // outcome depends on which one the server sees last. One write per cell at a time.
      if (busy.has(key)) return;
      // The grid already draws this cell refused; the guard is here so a press that reaches the hook
      // by any other route still cannot send a request `AssignCheckInAsync` is certain to refuse.
      if (cellRefusal(column, customerId) !== null) return;
      const held = holdsSurvey(column, customerId);
      const client = view.rows.find((row) => row.customerId === customerId)?.name ?? '';

      setWriteFailed(false);
      setBusy((open) => new Set(open).add(key));
      write.mutate(
        { templateId: column.id, customerId, held },
        {
          onSuccess: () => {
            announce(
              t(held ? 'web:reports.matrix.unassigned' : 'web:reports.matrix.assigned', {
                client,
                survey: column.title,
              }),
            );
            // The templates key, because `assignedCustomerIds` is what every tick in this grid is
            // drawn from — and the card's own figures with it.
            void queryClient
              .invalidateQueries({ queryKey: surveyTemplatesQueryKey })
              .finally(() => {
                release(key);
              });
          },
          onError: () => {
            setWriteFailed(true);
            release(key);
          },
        },
      );
    },
    writeFailed,
    onRetry: () => {
      void templates.refetch();
      void intake.refetch();
      void roster.refetch();
    },
  };
}
